/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Button, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  firstName?: string;
  programName?: string;
  onStartApplication?: () => void;
};

const styles = {
  heading: css`
    margin-bottom: 0;
  `,
  whatsNext: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-150);
  `,
  list: css`
    list-style: none;
    margin: 0 0 var(--cds-spacing-200);
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  listItem: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  iconBadge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--cds-color-feedback-success);
  `,
  // Grayed checkmark for checklist steps the learner hasn't completed yet.
  iconBadgePending: css`
    background: var(--cds-color-emphasis-neutral-background-default);
  `,
};

const getHeading = (programName?: string, firstName?: string) => {
  if (programName && firstName) {
    return _t("You're one step closer to the #{programName}, #{firstName}", { programName, firstName });
  }
  if (programName) {
    return _t("You're one step closer to the #{programName}", { programName });
  }
  if (firstName) {
    return _t("You're one step closer, #{firstName}", { firstName });
  }
  return _t("You're one step closer");
};

const SurveyTerminalScreen = ({ firstName, programName, onStartApplication }: Props) => {
  const checklistItems = [
    _t('Check your inbox for more details about the program'),
    _t('Hear from our enrollment team for personalized guidance and support'),
    _t("Start your application anytime — we'll guide you through the process"),
  ];

  return (
    <div data-testid="survey-terminal-screen">
      <Typography2 component="h3" variant="titleSmall" css={styles.heading}>
        {getHeading(programName, firstName)}
      </Typography2>
      <Typography2 component="p" variant="bodyPrimary" css={styles.whatsNext}>
        {_t("What's next:")}
      </Typography2>
      <ul css={styles.list}>
        {checklistItems.map((item, index) => (
          <li key={item} css={styles.listItem}>
            <span css={[styles.iconBadge, index > 0 && styles.iconBadgePending]} aria-hidden="true">
              <CheckIcon size="small" color="invert" />
            </span>
            <Typography2 component="span" variant="bodyPrimary">
              {item}
            </Typography2>
          </li>
        ))}
      </ul>
      {onStartApplication && (
        <Button variant="primary" onClick={onStartApplication}>
          {_t('Start application')}
        </Button>
      )}
    </div>
  );
};

export default SurveyTerminalScreen;
